import React from 'react';
import { FaBrain, FaRobot, FaEye, FaChartBar, FaCheck, FaCogs, FaCode } from 'react-icons/fa';

const CustomAISolutions = () => {
  const solutions = [
    {
      icon: <FaBrain />,
      title: 'Machine Learning Models',
      description: 'Custom-built ML models that learn and adapt to your specific business needs, from predictive maintenance to customer behavior analysis.'
    },
    {
      icon: <FaRobot />,
      title: 'Natural Language Processing',
      description: 'Advanced NLP solutions for text analysis, sentiment analysis, chatbots, and automated document processing.'
    },
    {
      icon: <FaEye />,
      title: 'Computer Vision',
      description: 'Sophisticated image and video analysis systems for object detection, facial recognition, and quality control automation.'
    },
    {
      icon: <FaChartBar />,
      title: 'Predictive Analytics',
      description: 'Data-driven forecasting models that help you anticipate market trends, optimize operations, and make informed decisions.'
    }
  ];

  const features = [
    'Custom Model Architecture',
    'Scalable Infrastructure',
    'Real-time Processing',
    'Cloud Integration',
    'API Development',
    'Performance Monitoring',
    'Model Retraining',
    'Security Implementation'
  ];

  const industries = [
    {
      title: 'Manufacturing',
      applications: 'Quality control, predictive maintenance, supply chain optimization'
    },
    {
      title: 'Healthcare',
      applications: 'Disease prediction, medical imaging analysis, patient care optimization'
    },
    {
      title: 'Finance',
      applications: 'Risk assessment, fraud detection, algorithmic trading'
    },
    {
      title: 'Retail',
      applications: 'Inventory management, customer behavior analysis, personalized recommendations'
    }
  ];

  return (
    <div className="min-h-screen bg-nvidia-dark">
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-nvidia-dark to-nvidia-dark-800">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Custom AI Solutions
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Harness the power of artificial intelligence with our tailored solutions. 
              We build custom AI systems that solve your unique business challenges and 
              drive innovation across your organization.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 bg-nvidia-dark-800">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center">Our AI Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                className="p-8 rounded-lg bg-nvidia-dark transform hover:-translate-y-1 transition-transform duration-300 border border-nvidia-green/20"
              >
                <div className="text-nvidia-green text-3xl mb-4">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{solution.title}</h3>
                <p className="text-gray-400">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-16 bg-gradient-to-b from-nvidia-dark-800 to-nvidia-dark">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Development Features</h2>
            <div className="bg-nvidia-dark-800 rounded-lg p-8 border border-nvidia-green/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <FaCheck className="text-nvidia-green mr-3" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-16 bg-nvidia-dark">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center">Industry Applications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="p-6 rounded-lg bg-nvidia-dark-800 border border-nvidia-green/20"
              >
                <h3 className="text-xl font-semibold mb-3 text-nvidia-green">{industry.title}</h3>
                <p className="text-gray-400">{industry.applications}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-nvidia-dark to-nvidia-dark-800">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Build Your Custom AI Solution?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how our custom AI solutions can transform your business operations and drive growth.
            </p>
            <button className="bg-nvidia-green text-black px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors">
              Start Your Project
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomAISolutions;
